import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdLabelDto } from '@/services/ws-labels/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { useTaskTablesContext } from '../../context';

export const LabelFilter: FC = () => {
  const [t] = useTranslation(['backend', 'common']);
  const filters = useTaskTablesContext('filters$');
  const { updateFilter, labelPrefix, setLabelPrefix, labelRequest } = useTaskTablesContext();

  const options = useSubscribe(
    labelRequest.data$,
    (response) => response?.data.items.flatMap((item) => (item.id ? { label: item.name, value: item.id } : [])) ?? [],
  );
  const total = useSubscribe(labelRequest.data$, (response) => response?.data.total ?? 0);
  const loading = useSubscribe(labelRequest.pending$);

  const handleChange = useCallbackRef((option: Option<IdLabelDto> | null) => {
    updateFilter('label', option);
  });

  const serverSideFiltering = useMemo(
    () => ({ filter: labelPrefix, setFilter: setLabelPrefix, total }),
    [labelPrefix, setLabelPrefix, total],
  );

  return (
    <Filter<IdLabelDto>
      label={t('common:LABEL')}
      options={options}
      selected={filters.label}
      onChange={handleChange}
      loading={loading}
      serverSideFiltering={serverSideFiltering}
      isClearable
      dataTestId='label-filter'
    />
  );
};
